import React, { CSSProperties } from 'react';
import Style from './index.less';
import classNames from 'classnames';
import { IReactionDisposer, reaction } from 'mobx';
import i18n from '@i18next/i18n';
import {
    ANIM_TYPE_TEXT,
    ANIM_TO_SUB_DIR,
} from '@src/js/js-keyframe-animation/style-converter/constants';
import {
    presetClassTypes,
    MEDIA_ANIM_TYPES,
} from '@src/slide/domain/ui-model/element-modeles/property/animation/constant';
import { ANIM_REPORT } from '../constants';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import { ANIM_DRAG_ICON } from '../images';
import { getElementDetailDisplayInfo } from '@src/utils/get-display-name';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { AnimType, PresetSubtype } from '@tencent/slide-sdk-model';
import AnimItemHeader from './components/anim-item-header';
import AnimItemBoard from './components/anim-item-board';
import MediaAnimItemBoard from '@com/animation-editor/animationItem/components/media-anim-item-board';

type TModifyBy = string | number | { x: number; y: number; };

type IAnimationItemProps = {
    canEdit: boolean;
    data: any;
    isShown: boolean;
    triggerSelectOptions: {value: string, name: string}[];
    style: CSSProperties;
    isGroupFirstItem?: boolean;
    selectionService?: ISelectionService;
    onPlay: () => void;
    onMove: () => void;
    onDelete: () => void;
    onSelected?: (e) => void;
    onModify?: (props: { delay?: number, duration?: number, clickSpid?: string | string[], animType?: string | number,
        subType?: number, nodeType?: number, by?: TModifyBy, opacity?: string,
        audio?: { repeatCount?: string, numSld?: number }
    }) => void;
    onVisibleChange?: (visible: boolean) => void;
    onDragPositionChange?: (pos: number) => void;
};

type IAnimationItemState = {
    isSelected: boolean;
    isHover:  boolean;
    isDragHover:  boolean;
    isShown: boolean;
    isDragging:  boolean;
    hasAnimation:  boolean;
    contentHeight: number;
    showSubType:  boolean;
    subTypes: number[];
    currentSubType: string | number;
    draggable:  boolean;
};

/**
 * 动画编辑组件，可展开或者折叠内容块
 */
export default class AnimationItem extends React.Component<IAnimationItemProps, IAnimationItemState> {
    private domElement;
    private selectionRefreshAction: IReactionDisposer | null;
    private dataRefreshAction: IReactionDisposer | null;
    private mounted: boolean;

    public constructor(props, context) {
        super(props, context);

        this.state = {
            isSelected: props.data.selected,
            isHover: false,
            isDragHover: false,
            isShown: props.isShown,
            isDragging: false,
            hasAnimation: false,  // 是否需要动画
            contentHeight: 168,
            showSubType: false, // 是否展示效果选项
            subTypes: [], // 效果选项
            currentSubType: '',
            draggable: true,
        };
    }


    public animTypeExist() {
        const { animType } = this;
        if (!(animType && ANIM_TO_SUB_DIR[animType]?.length)) return;
        // 浮入浮出的subType为0,需要通过animType对应到向上，向下
        const animTypeMap = {
            [AnimType.FloatUpIn]: PresetSubtype.Bottom,
            [AnimType.FloatUpOut]: PresetSubtype.Bottom,
            [AnimType.FloatDownIn]: PresetSubtype.Top,
            [AnimType.FloatDownOut]: PresetSubtype.Top,
        };
        const currentSubType = animTypeMap[animType] ? animTypeMap[animType] : this.props.data.props.subType;
        this.setState({
            showSubType: true,
            subTypes: ANIM_TO_SUB_DIR[animType],
            currentSubType,
        });
        // 放大/缩小, 陀螺旋的subType为0,需要通过animType和by字段获得currentSubType
        if (animType === AnimType.Scale || animType === AnimType.Rotate) {
            this.handleScaleAndRotate();
        }
    }

    public initSelectionRefreshAction() {
        this.selectionRefreshAction = reaction(
            () => this.props.data.selected,
            (selected) => {
                if (!this.mounted) return;
                this.setState({}, () => {
                    // 是否需要滚动到显示区域
                    if (selected && this.props.data.needScroll) {
                        this.props.data.needScroll = false;
                        this.domElement?.scrollIntoView?.({ block: 'end',  behavior: 'smooth' });
                        document.body.scrollTop = 0;
                    }
                });
            },
        );
    }

    public initDataRefreshAction() {
        this.dataRefreshAction = reaction(() => (
            {
                animType: this.props.data.props.animType,
                currentSubType: this.props.data.props.subType,
            }
        ), ({ animType, currentSubType }) => {
            if (!this.mounted) return;
            if (ANIM_TO_SUB_DIR[animType]?.length) {
                this.animTypeExist();
            } else {
                this.setState({
                    showSubType: false,
                    subTypes: [],
                });
            }
        });
    }

    public componentDidMount() {
        this.mounted = true;
        this.animTypeExist();
        this.initSelectionRefreshAction();
        this.initDataRefreshAction();

        // 滚动到显示区域
        if (this.props.data.selected && this.props.data.needScroll) {
            this.props.data.needScroll = false;
            this.domElement?.scrollIntoView?.({ block: 'end',  behavior: 'smooth' });
            document.body.scrollTop = 0;
        }
    }

    public componentWillUnmount() {
        this.mounted = false;
        this.selectionRefreshAction?.();
        this.dataRefreshAction?.();
    }

    public componentDidUpdate() {
        if (this.state.hasAnimation) {
            this.setState({
                hasAnimation: false,
            });
        }
    }

    public handleScaleAndRotate = () => {
        const { by } = this.props.data.props;
        let currentSubType: number| string = '';
        currentSubType = this.handleScaleAndRotateXYNotExist(by);
        if (by?.x && by?.y) {
            // 放大/缩小
            if ((String(by.x) !== '1' && String(by.y) !== '1') || (String(by.x) === '1' && String(by.y) === '1')) {
                currentSubType = PresetSubtype.Total;
            } else if (String(by.x) === '1') {
                currentSubType = PresetSubtype.TopBottom;
            } else {
                currentSubType = PresetSubtype.LeftRight;
            }
        }
        this.setState({
            currentSubType,
        });
    };

    public handleScaleAndRotateXYNotExist(by) {
        let stackCurrentSubType: number| string = '';
        if (by && !by.x && !by.y) {
            // 陀螺旋
            if (parseInt(by, 10) > 0) {
                stackCurrentSubType = PresetSubtype.Clockwise;
            } else {
                stackCurrentSubType = PresetSubtype.Counterclockwise;
            }
        }
        return stackCurrentSubType;
    }

    public onVisibleChange = (visible) => {
        this.props.onVisibleChange?.(visible);
    };

    public handleHeaderClick = (e) => {
        if (!this.props.canEdit) {
            Snackbar.show({
                message: i18n.t('暂不支持编辑和播放'),
                autoClose: true,
                closable: true,
            });
            return;
        }
        // 多选及点击到按钮时不触发展开收起逻辑
        if (!(e.metaKey || e.ctrlKey || e.shiftKey)) {
            if (this.state.isDragging) return;
            this.onVisibleChange(!this.props.isShown);
        }
    };
    public handleSelect = (e) => {
        if (!this.props.canEdit) return;
        this.setState({
            isSelected: true,
            hasAnimation: true,
        });
        this.props.onSelected?.(e);
    };

    public handlePlay = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onPlay();
    };

    public handleDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onDelete();
    };

    public handleMouseEnter = (e) => {
        if (!this.props.canEdit) return;
        e.preventDefault();
        e.stopPropagation();
        if (!this.state.isDragging && !this.state.isHover) {
            this.setState({
                isHover: true,
                isDragHover: false,
            });
        }
    };

    public handleMouseLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.state.isHover) {
            this.setState({
                isHover: false,
            });
        }
    };

    public handleDragStart = (e) => {
        if (!this.props.canEdit) return;
        e.dataTransfer.setData('text', '');
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.dropEffect = 'move';
        if (!this.state.isDragging) {
            this.setState({
                isDragging: true,
                isHover: false,
                isDragHover: false,
            });
        }
    };

    public handleDragEnd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.state.isDragging) {
            this.props.onMove();
            this.setState({
                isDragging: false,
                isDragHover: false,
            });
        }
    };

    public handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.state.isDragging && !this.state.isDragHover) {
            this.setState({
                isDragHover: true,
            });
        }
    };
    public handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.state.isDragHover) {
            this.setState({
                isDragHover: false,
            }, () => {
                this.props.onDragPositionChange?.(-1);
            });
        }
    };

    public elementSelected = () => {
        if (!this.elementModel) return false;
        return this.getSelectionService()?.selectedElements._findItem(this.props.data.elementModel);
    };


    public getDisabledAnim = (animType, animClass) => {
        // 路径动画不支持切换
        if (animClass === presetClassTypes.PATH) return true;
        if (this.isSelect3D() && [AnimType.EntrBlinds, AnimType.ExitBlinds].includes(animType)) {
            // 3d 百叶窗动画有问题，暂时屏蔽
            return true;
        }
        return false;
    };

    public handleValueEnter = () => {
        if (this.state.draggable) {
            this.setState({
                draggable: false,
            });
        }
    };

    public handleValueLeave = () => {
        if (!this.state.draggable) {
            this.setState({
                draggable: true,
            });
        }
    };

    public isDraggable() {
        return this.state.draggable && this.props.canEdit;
    }

    public getShowSelectedState() {
        return (this.props.data.selected || this.elementSelected()) && !this.state.isDragHover;
    }

    public getItemClass() {
        const showSelectedState = this.getShowSelectedState();
        return classNames({
            [Style.unfold]: true,
            [Style.selected]: showSelectedState,
        });
    }

    public getContainerClass() {
        return classNames({
            [Style.container]: true,
        });
    }

    public render() {
        const dragStyle = this.isDragging ? { opacity: 0.24 } : {};
        const { name, icon } = getElementDetailDisplayInfo({
            elementModel: this.elementModel,
            useSdkName: false,
        });

        const headerVisible = this.state.isHover || this.getShowSelectedState();

        return (
            <div className={Style['anim-item']}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                draggable={this.isDraggable()}
                onMouseDown={this.handleSelect}
                onDragStart={this.handleDragStart}
                onDragEnd={this.handleDragEnd}
                onDragOver={this.handleDragOver}
                style={{ ...dragStyle }}
            >
                <div style={{ cursor: 'pointer' }}>
                    <div className={Style['item-left']}  style={{ pointerEvents: 'none' }}>
                        {
                            this.state.isHover || this.getShowSelectedState()
                                ? <img src={ANIM_DRAG_ICON} style={{ width: 16, height: 16 }}></img>
                                : <span className={Style['item-order']}>{this.props.data.order}</span>
                        }
                    </div>
                </div>
                <div className={this.getItemClass()}
                    ref={(e) => {
                        this.domElement = e;
                    }}
                    style={this.props.style}
                >
                    <div className={this.getContainerClass()}>
                        <AnimItemHeader
                            visible={headerVisible}
                            animType={this.animType}
                            name={name}
                            icon={icon}
                            onPlay={this.props.onPlay}
                            onDelete={this.props.onDelete}
                            onHeaderClick={this.handleHeaderClick}
                        />
                        { this.showAnimBoard(this.animType)
                            ? <AnimItemBoard
                                visible={this.props.isShown}
                                animType={this.animType}
                                subType={`${this.state.currentSubType}`}
                                sTypes={this.state.subTypes}
                                triggerSelectOptions={this.props.triggerSelectOptions}
                                isShown={this.props.isShown}
                                contentHeight={this.state.contentHeight}
                                animationModel={this.props.data}
                                selectionService={this.getSelectionService()}
                                onModify={this.props.onModify}
                                onSlideValueEnter={this.handleValueEnter}
                                onSlideValueLeave={this.handleValueLeave}
                            />
                            : <MediaAnimItemBoard
                                visible={this.props.isShown}
                                isShown={this.props.isShown}
                                contentHeight={this.state.contentHeight}
                                animationModel={this.props.data}
                                onModify={this.props.onModify}
                            />}
                    </div>
                </div>
            </div>
        );
    }

    public get isDragging(): boolean {
        return this.state.isDragging && this.state.isDragHover;
    }

    public get elementModel() {
        return this.props.data.elementModel;
    }

    public get animType(): AnimType {
        return this.props.data.props.animType;
    }

    private tdwModifyReport(report) {
        tdw({ ...ANIM_REPORT, ver7: this.elementModel.type, ...report });
    }

    private showAnimBoard = (animType) => {
        if (!this.checkNotMedia(animType) || !ANIM_TYPE_TEXT[animType]) return false;
        return true;
    };

    private checkNotMedia = (animType: AnimType): boolean => !Object.values(MEDIA_ANIM_TYPES).includes(animType);

    private getSelectionService() {
        return this.props.selectionService;
    }
    private isSelect3D() {
        return this.elementModel?.scene3d || this.elementModel?.shape3d;
    }
}
